import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { HxIcon } from '../../../controls/icon/icon';
import { getIconSprite } from '../../../controls/icon/icon-sprite';
import { DOCUMENT } from '@angular/common';
import { StopPropagationDirective } from '../../../directives';
import { HxLabel } from '../../label/label';
import { HxPopoverDirective } from '../../popover';
import { HxTooltipDirective } from '../../tooltip/tooltip';
import { HxNodeStatus, HxTreeConfig, HxTreeNode } from '../tree.types';

@Component({
  selector: 'hx-tree-node',
  standalone: true,
  imports: [CommonModule, HxIcon, HxLabel, HxPopoverDirective, HxTooltipDirective, StopPropagationDirective],
  templateUrl: './tree-node.html',
  styleUrls: ['../tree.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[style.--actual-row-height.px]': 'actualRowHeight',
    // Color tokens are now managed via CSS only
    // ...existing code...
  },
})
export class HxTreeNodeComponent implements AfterViewInit {
  private readonly iconSprite = getIconSprite(inject(DOCUMENT));
  private readonly router = inject(Router, { optional: true });

  readonly node = input.required<HxTreeNode>();
  readonly level = input.required<number>();
  readonly isLast = input<boolean>(false);
  readonly collapsed = input<boolean>(false);

  readonly config = input.required<HxTreeConfig>();
  readonly expandedIds = input.required<Set<string>>();
  readonly activeNodeId = input.required<string | null>();
  readonly focusedNodeId = input<string | null>(null);
  readonly readonly = input<boolean>(false);
  readonly showConnectorLines = input<boolean>(true);
  readonly nodeTemplate = input<TemplateRef<any> | null>(null);

  // --- General Styling Inputs ---
  readonly nodeTextColor = input<string>('');
  readonly nodeBackgroundColor = input<string>('');
  readonly nodeIconColor = input<string>('');
  readonly nodeHoverBackgroundColor = input<string>('');

  // --- Toggles ---
  readonly showStatusIcons = input<boolean>(true);
  readonly showChildIcons = input<boolean>(false);

  // --- Active Colors ---
  readonly activeChildBackgroundColor = input<string>('');
  readonly activeRootBackgroundColor = input<string>('');

  // --- Connector Lines ---
  readonly errorConnectorLineColor = input<string>('');
  readonly successConnectorLineColor = input<string>('');
  readonly defaultConnectorLineColor = input<string>('');
  readonly useUniformConnectorLineColor = input<boolean>(false);

  // --- Status Backgrounds ---
  readonly errorBackgroundColor = input<string>('');
  readonly successBackgroundColor = input<string>('');
  readonly defaultBackgroundColor = input<string>('');
  readonly useUniformBackgroundColor = input<boolean>(false);

  readonly nodeSelect = output<HxTreeNode>();
  readonly toggleId = output<string>();

  readonly contentRef = viewChild<ElementRef<HTMLDivElement>>('nodeContent');

  /* popover */
  readonly popoverOpen = signal(false);

  /* derived */
  readonly isCollapsed = computed(() => this.collapsed());
  readonly hasChildren = computed(() => !!this.node().children?.length);
  // readonly inferredStatus = computed(() => this.node().status);

  readonly shouldShowChevron = computed(() => !this.isCollapsed() && this.hasChildren());

  readonly shouldShowErrorIcon = computed(
    () => this.shouldShowStatusIconsForNode() && this.inferredStatus() === 'error'
  );

  readonly shouldShowCollapsedIndicator = computed(
    () =>
      this.isCollapsed() &&
      (this.isActive() || this.hasActiveDescendant()) &&
      this.inferredStatus() !== 'success' &&
      !this.shouldShowErrorIcon()
  );

  readonly shouldShowActiveIndicator = computed(
    () =>
      !this.isCollapsed() &&
      this.isActive() &&
      !this.hasChildren() &&
      this.inferredStatus() !== 'success' &&
      !this.shouldShowErrorIcon()
  );

  readonly shouldRenderChildren = computed(() => !this.isCollapsed() && this.hasChildren());

  readonly shouldShowMainActiveIndicator = computed(
    () => this.shouldShowCollapsedIndicator() || this.shouldShowActiveIndicator()
  );

  openPopover(): void {
    if (this.isCollapsed()) {
      this.preloadIconSprite();
      this.popoverOpen.set(true);
    }
  }

  closePopover(): void {
    this.popoverOpen.set(false);
  }

  actualRowHeight = 44;

  constructor() {
    effect(() => {
      if (this.focusedNodeId() === this.node().id) {
        const contentElement = this.contentRef()?.nativeElement;

        // Only apply focus management during keyboard navigation, never steal from form controls
        if (contentElement) {
          const activeElement = document.activeElement as HTMLElement | null;
          // Never steal focus from controls outside the tree (e.g. within assessment content or other components)
          if (activeElement && activeElement !== document.body) {
            const treeHost = contentElement.closest('.hx-tree-root, hx-tree');
            if (treeHost && !treeHost.contains(activeElement)) {
              return;
            }

            const tagName = activeElement.tagName.toLowerCase();
            const isFormField =
              ['input', 'textarea', 'select', 'button'].includes(tagName) ||
              activeElement.matches(
                'hx-radio, hx-checkbox, [role="radio"], [role="checkbox"], [role="combobox"], [role="listbox"], [role="table"], [role="grid"], [role="row"], [role="gridcell"], [tabindex]'
              );

            // Check if element is contenteditable (catches Quill editor, chip control, and any other rich text)
            const isContentEditable =
              activeElement.contentEditable === 'true' || !!activeElement.closest('[contenteditable="true"]');

            // Also check for specific editor UI containers (note editor overlay, toolbar, etc.)
            const isEditorUI =
              !!activeElement.closest('.note-editor-overlay') ||
              !!activeElement.closest('hx-text-editor') ||
              !!activeElement.closest('.ql-toolbar') ||
              !!activeElement.closest('.ql-tooltip');

            if (isFormField || isContentEditable || isEditorUI) {
              return;
            }
          }

          contentElement.focus({ preventScroll: true });
        }
      }
    });
  }

  ngAfterViewInit() {
    this.measureRowHeight();

    // Warm icon sprite once so first collapsed-popover open does not wait on icon asset fetch.
    if (this.isCollapsed()) {
      this.preloadIconSprite();
    }

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => this.measureRowHeight());
      const element = this.contentRef()?.nativeElement;
      if (element) resizeObserver.observe(element);
    }
  }

  private measureRowHeight() {
    const element = this.contentRef()?.nativeElement;
    if (element) {
      this.actualRowHeight = element.offsetHeight;
    }
  }

  readonly isExpanded = computed(() => this.expandedIds().has(this.node().id));
  readonly isActive = computed(() => this.activeNodeId() === this.node().id);
  readonly isFocused = computed(() => this.focusedNodeId() === this.node().id);

  readonly shouldShowIconSpace = computed(() => {
    if (this.level() > 0) {
      return this.showChildIcons() ? this.hasIconInTree(this.node()) : false;
    }
    return true;
  });

  readonly shouldShowStatusIconsForNode = computed(() => {
    // Hide status icons for children if showChildIcons is false
    if (this.level() !== 0 && !this.showChildIcons()) return false;
    return this.showStatusIcons();
  });

  readonly hasActiveDescendant = computed(() => {
    const activeId = this.activeNodeId();
    if (!activeId) return false;
    return this.isDescendant(this.node(), activeId);
  });

  readonly inferredStatus = computed<HxNodeStatus>(() => {
    if (!this.showStatusIcons()) return 'default';
    const n = this.node();
    const children = n.children;
    // n.status reflects the node's own direct items (e.g. a section's direct questions
    // that aren't wrapped in a sub-section), so it must hold alongside child statuses -
    // otherwise incomplete direct questions get masked by fully-successful children.
    const isSuccess = children?.length
      ? n.status === 'success' && children.every((c) => c.status === 'success')
      : n.status === 'success';
    return isSuccess ? 'success' : 'error';
  });

  readonly labelColor = computed<string | undefined>(() => {
    // In expanded nav, active root rows need explicit label color to maintain contrast
    // when status classes are also present.
    const isExpandedRootRow =
      !this.isCollapsed() && this.level() === 0 && (this.hasActiveDescendant() || this.isActive());
    if (!isExpandedRootRow) {
      return undefined;
    }

    return 'var(--tree-active-root-text)';
  });

  readonly displayLabel = computed(() => this.normalizeLabel(this.node().label));

  private normalizeLabel(label: string | null | undefined): string {
    const raw = String(label ?? '');
    const withoutTags = raw.replace(/<[^>]*>/g, ' ');
    const withoutEntities = withoutTags.replace(/&nbsp;/gi, ' ');
    const withoutZeroWidth = withoutEntities.replace(/[\u200B-\u200D\uFEFF]/g, '');
    const normalized = withoutZeroWidth.trim();
    return normalized.length > 0 ? normalized : 'Untitled';
  }

  readonly containerClass = computed(() => {
    const isRoot = this.level() === 0;
    const classes: string[] = [];

    if (isRoot) classes.push('is-root');
    const status = this.inferredStatus();
    if (status !== 'default') {
      if (isRoot) classes.push(`hx-section-${status}`);
      classes.push(status);
    }

    if (this.isLast()) classes.push('is-last-child');
    if (this.node().disabled) classes.push('hx-nav-node--disabled');
    if (!this.showConnectorLines()) classes.push('hx-node--no-lines');

    return classes.join(' ');
  });

  readonly contentClass = computed(() => {
    if (this.readonly()) return '';

    const isRoot = this.level() === 0;
    const classes: string[] = [];

    // Always apply active state classes for proper CSS hover/active behavior
    if (isRoot) {
      if (this.hasActiveDescendant() || this.isActive()) {
        classes.push('hx-bg-root-active');
      }
    } else {
      if (this.isActive()) {
        classes.push('hx-bg-child-active');
      }
    }
    return classes.join(' ');
  });

  readonly lineColorVar = computed(() => {
    const status = this.inferredStatus();

    // 1. Uniform override
    if (this.useUniformConnectorLineColor() && this.defaultConnectorLineColor()) {
      return this.defaultConnectorLineColor();
    }

    // 2. Specific color overrides
    if (status === 'error' && this.errorConnectorLineColor()) return this.errorConnectorLineColor();
    if (status === 'success' && this.successConnectorLineColor()) return this.successConnectorLineColor();
    if (status === 'default' && this.defaultConnectorLineColor()) return this.defaultConnectorLineColor();

    // 3. Fallback to CSS tokens (use dedicated line tokens with status fallbacks)
    if (this.readonly()) return 'var(--tree-line-default)';
    if (this.isActive()) return 'var(--tree-line-active)';
    if (status === 'error') return 'var(--tree-line-error, var(--tree-status-error-main))';
    if (status === 'success') return 'var(--tree-line-success, var(--tree-status-success-main))';
    return 'var(--tree-line-default)';
  });

  readonly customStyles = computed(() => {
    const styles: { [key: string]: string } = {};

    // Pass hover background color to the CSS variable used by hover state
    if (this.nodeHoverBackgroundColor()) {
      styles['--node-hover-color'] = this.nodeHoverBackgroundColor();
    }

    // Pass active colors for use in content styling
    if (this.activeChildBackgroundColor()) {
      styles['--node-active-color'] = this.activeChildBackgroundColor();
    }
    if (this.activeRootBackgroundColor()) {
      styles['--node-active-parent-color'] = this.activeRootBackgroundColor();
    }

    // Determine background color based on state
    const isRoot = this.level() === 0;
    const isActiveNode = (isRoot && (this.isActive() || this.hasActiveDescendant())) || (!isRoot && this.isActive());
    const status = this.node().status || 'default';

    let bgColor = '';

    // 1. Apply active state colors if node is active
    if (isActiveNode) {
      if (isRoot && this.activeRootBackgroundColor()) {
        bgColor = this.activeRootBackgroundColor();
      } else if (!isRoot && this.activeChildBackgroundColor()) {
        bgColor = this.activeChildBackgroundColor();
      }
    }

    // 2. If no active color, apply status-based background
    if (!bgColor) {
      if (this.useUniformBackgroundColor() && this.defaultBackgroundColor()) {
        bgColor = this.defaultBackgroundColor();
      } else {
        bgColor =
          status === 'error'
            ? this.errorBackgroundColor()
            : status === 'success'
              ? this.successBackgroundColor()
              : this.defaultBackgroundColor();
      }
    }

    // 3. Always apply nodeBackgroundColor if provided (base color)
    if (this.nodeBackgroundColor()) {
      // Only override if we don't have active/status-based colors
      if (!bgColor) {
        bgColor = this.nodeBackgroundColor();
      }
    }

    if (bgColor) {
      styles['backgroundColor'] = bgColor;
    }

    return styles;
  });

  onContentClick(event: Event): void {
    if (this.readonly()) return;

    const n = this.node();
    if (n.disabled) return;

    if (this.hasChildren()) {
      if (!this.isExpanded()) this.toggleId.emit(n.id);
      const firstChild = n.children?.[0];
      if (firstChild) this.nodeSelect.emit(firstChild);
    } else {
      this.nodeSelect.emit(n);
    }
  }

  routeHref(route: HxTreeNode['route'], node: HxTreeNode = this.node()): string | null {
    if (node.externalUrl) {
      return node.externalUrl;
    }

    if (!route || this.config().navigationType !== 'route' || node.disabled || this.readonly()) {
      return null;
    }

    if (this.router) {
      const commands = Array.isArray(route) ? route : [route];
      return this.router.serializeUrl(this.router.createUrlTree(commands));
    }

    const path = Array.isArray(route) ? route.join('/') : route;
    return path.startsWith('/') ? path : `/${path}`;
  }

  onRouteLinkClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.node().disabled || this.readonly()) {
      event.preventDefault();
      return;
    }

    if (this.node().externalUrl) {
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      const href = this.routeHref(this.node().route);
      if (href && typeof window !== 'undefined') {
        window.open(href, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    // Preserve native middle-click, Shift-click, and Alt-click behavior.
    if (event.button === 1 || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    this.onContentClick(event);
  }

  onPopoverRouteLinkClick(child: HxTreeNode, event: MouseEvent): void {
    event.stopPropagation();
    if (child.disabled || this.readonly()) {
      event.preventDefault();
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      const href = this.routeHref(child.route);
      if (href && typeof window !== 'undefined') {
        window.open(href, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    if (child.externalUrl) {
      return;
    }

    if (event.button === 1 || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    this.onPopoverItemClick(child, event);
  }

  onToggle(event: Event): void {
    if (this.readonly()) return;
    this.toggleId.emit(this.node().id);
  }

  onPopoverItemClick(child: HxTreeNode, event: Event): void {
    if (this.readonly() || child.disabled) return;

    // Close popover by emitting the selection
    this.closePopover();

    // Emit the child node selection for navigation
    this.nodeSelect.emit(child);
  }

  getPopoverStatus(child: HxTreeNode): HxNodeStatus {
    if (!this.showStatusIcons()) {
      return child.status || 'default';
    }
    return child.status === 'success' ? 'success' : 'error';
  }

  shouldShowPopoverActiveIndicator(child: HxTreeNode): boolean {
    const status = this.getPopoverStatus(child);
    return this.activeNodeId() === child.id && status !== 'success' && status !== 'error';
  }

  shouldShowPopoverStatusIcon(child: HxTreeNode, status: 'success' | 'error'): boolean {
    if (!this.showStatusIcons()) return false;
    return this.getPopoverStatus(child) === status;
  }

  shouldRenderPopoverActions(child: HxTreeNode): boolean {
    return this.shouldShowPopoverActiveIndicator(child) || this.showStatusIcons();
  }

  private preloadIconSprite(): void {
    this.iconSprite.load();
  }

  private isDescendant(parent: HxTreeNode, targetId: string): boolean {
    if (!parent.children) return false;
    for (const child of parent.children) {
      if (child.id === targetId) return true;
      if (this.isDescendant(child, targetId)) return true;
    }
    return false;
  }

  private hasIconInTree(node: HxTreeNode): boolean {
    if (node.icon) return true;
    if (!node.children) return false;
    return node.children.some((child) => this.hasIconInTree(child));
  }
}
